import { type RefObject} from "react"
import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import BigNumber from "bignumber.js"

import {UsernameType} from "@/lib/constants"

/**
 * Combina clases CSS utilizando clsx y tailwind-merge.
 * 
 * @param inputs - Lista de valores de clase a combinar
 * @returns String con las clases combinadas y optimizadas
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Convierte un texto en un slug URL-friendly.
 * 
 * @param text - Texto a convertir en slug
 * @returns String con el texto convertido en formato slug
 */
export function slugify(text: string): string {
  return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/--+/g, '-')
}

/**
 * Cuenta el número de palabras en un texto.
 * 
 * @param text - Texto del cual contar palabras
 * @returns Número de palabras en el texto
 */
export const countWords = (text: string) => {
  const words = text.split(/\s+/).filter(Boolean)
  return words.length
}

/**
 * Determina el tipo de nombre de usuario basado en su formato.
 * 
 * @param username - Nombre de usuario a analizar
 * @returns Tipo de nombre de usuario (EMAIL, INTERNATIONAL_PHONE, PHONE o UNKNOWN)
 */
export function determineUsernameType(username: string): UsernameType {
  if (username.includes("@")) {
    return UsernameType.EMAIL;
  } else if (username.match(/^\+\d{1,3}\d{10}$/)) {
    return UsernameType.INTERNATIONAL_PHONE;
  } else if (username.match(/^\d{10}$/)) {
    return UsernameType.PHONE;
  } else {
    return UsernameType.UNKNOWN;
  }
}

/**
 * Formatea un número según el formato de moneda mexicana.
 * 
 * @param amount - Cantidad a formatear (número o BigNumber)
 * @returns String con el número formateado en formato de moneda mexicana
 */
export const numberFormat = (amount: number | BigNumber) => {
  const options = { currency: 'MXN'}
  const format = new Intl.NumberFormat('es-MX', options)
  if (amount instanceof BigNumber) {
    return format.format(amount.toNumber())
  }
  return format.format(amount)
}

/**
 * Extrae las iniciales del nombre y apellido de un usuario.
 * 
 * @param user - Objeto de usuario con propiedades de nombre, apellido, teléfono, email e imagen
 * @returns String con las iniciales del usuario
 */
export function extractInitials(user: { lastname: string; phone: string } & {
  name?: string | null;
  email?: string | null;
  image?: string | null
}): string {
  const nameInitial = user.name?.[0] ?? '';
  const lastnameInitial = user.lastname?.[0] ?? '';
  return nameInitial + lastnameInitial;
}

/**
 * Copia el texto de un elemento HTML al portapapeles.
 * 
 * @param areaRef - Referencia al elemento HTML cuyo texto se copiará
 */
export const copy2Clipboard = (areaRef: RefObject<HTMLElement>) => {
  const currentNode = areaRef.current
    if (!currentNode) return
    navigator.clipboard.writeText(currentNode.innerText).then(() => {})
}

/**
 * Formatea una fecha en formato ISO a un formato legible en español.
 * 
 * @param date - Fecha en formato ISO (YYYY-MM-DD)
 * @returns String con la fecha formateada en español (día de la semana, día, mes, año)
 */
export function formatDate(date: string): string {
  const [year, month, day] = date.split("-").map(Number)
  const dateObj = new Date(year, month - 1, day)
  const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
  }
  return dateObj.toLocaleDateString("es-ES", options).replace("de ", "del ")
}

/**
 * Formatea una fecha para mostrarla en el contexto de reservaciones.
 * 
 * @param dateParam - Fecha en formato ISO (YYYY-MM-DD)
 * @returns String con la fecha formateada para reservaciones (día de la semana, día, mes)
 */
export function formatReservationDate(dateParam: string): string {
  const [year, month, day] = dateParam.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  const options: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long' };
  const formattedDate = date.toLocaleDateString('es-MX', options);
  return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
}

/**
 * Formatea una hora en formato de 24 horas a formato de 12 horas con AM/PM.
 * 
 * @param hourParam - Hora en formato de 24 horas (HHMM)
 * @returns String con la hora formateada en formato de 12 horas con AM/PM
 */
export function formatReservationHour(hourParam: string): string {
  const hour = parseInt(hourParam.slice(0, 2), 10);
  const minutes = hourParam.slice(2, 4);
  const ampm = hour < 12 ? 'am' : 'pm';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
}

/**
 * Formatea una fecha ISO a un formato extenso en español.
 * 
 * @param isoString - Fecha en formato ISO
 * @returns String con la fecha formateada en español (día de la semana, día, mes abreviado, año)
 */
export function formatDateToSpanish(isoString: string): string {
  const date = new Date(isoString);

  // Days of the week in Spanish
  const daysOfWeek = [
    'Domingo',
    'Lunes',
    'Martes',
    'Miércoles',
    'Jueves',
    'Viernes',
    'Sábado'
  ];

  // Months in Spanish
  const months = [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sep',
    'Oct',
    'Nov',
    'Dic'
  ];

  const dayOfWeek = daysOfWeek[date.getDay()];
  const dayOfMonth = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  return `${dayOfWeek} ${dayOfMonth} de ${month} del ${year}`;
}

/**
 * Convierte una cadena base64 URL-safe a un array Uint8Array.
 * Útil para trabajar con Web Push API.
 * 
 * @param base64String - Cadena en formato base64 URL-safe
 * @returns Array Uint8Array con los datos decodificados
 */
export function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

/**
 * Convierte un color hexadecimal a formato RGBA.
 * 
 * @param hex - Color en formato hexadecimal (#RRGGBB)
 * @param alpha - Valor de transparencia (0-1), por defecto 1
 * @returns String con el color en formato RGBA
 */
export const hexToRGBA = (hex, alpha = 1) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Verifica si una cadena es una URL válida.
 * 
 * @param url - URL a validar
 * @returns Boolean indicando si la URL es válida
 */
export const isValidUrl = (url?: string | null): boolean => {
  if (!url) return false
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

/**
 * Verifica si un número es un número de WhatsApp válido.
 * 
 * @param number - Número a validar
 * @returns Boolean indicando si el número es válido para WhatsApp
 */
export function isValidWhatsAppNumber(number: string): boolean {
  const regex = /^\+?[1-9]\d{6,14}$/;
  return regex.test(number);
  }

/**
 * Capitaliza la primera letra de una cadena.
 * 
 * @param str - Cadena a capitalizar
 * @returns Cadena con la primera letra en mayúscula
 */
export function capitalizeFirstLetter(str) {
  if(!str) return
  if (str.length === 0) return str;
  if (!/[a-zA-Z]/.test(str.charAt(0))) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Verifica si una cadena es válida (no undefined, no null y no vacía).
 * 
 * @param value - Cadena a validar
 * @returns Boolean indicando si la cadena es válida
 */
export const validStr = (value: string) => {
  return typeof value !== "undefined" && value !== null && value !== ""
}

/**
 * Calcula la distancia entre dos puntos geográficos usando la fórmula de Haversine.
 * 
 * @param lat1 - Latitud del primer punto
 * @param lon1 - Longitud del primer punto
 * @param lat2 - Latitud del segundo punto
 * @param lon2 - Longitud del segundo punto
 * @returns Distancia en kilómetros entre los dos puntos
 */
export const getDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const toRad = (value: number) => (value * Math.PI) / 180;
  const R = 6371; // Radio de la Tierra en km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distancia en km
};

/**
 * Elimina las comillas simples y dobles de una cadena.
 * 
 * @param text - Cadena a procesar
 * @returns Cadena sin comillas simples y dobles
 */
export function removeQuotes(text: string): string {
  return text.replace(/['"]+/g, '');
}

export const dateToISO = (date: Date): string => {
  const offset = new Date().getTimezoneOffset() * 60000;
  const adjustedDate = new Date(date.getTime() - offset);
  return adjustedDate.toISOString().slice(0, -1);
}